import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { PLAYER_COLORS } from '../constants';
import { generateMap } from '../utils/gameLogic';
import { resolveRoomSettings, DEFAULT_TURN_TIMER_SECONDS } from '../utils/roomSettings';
import { createGameLog, finalizeGameLog, expandGameLog, recordPlaceMove, recordSwapMove } from '../utils/gameRecorder';
import {
  createRoom, createOrJoinRematch, joinRoom, rejoinGame, startGame,
  togglePlayerReady, addBotToRoom, leaveRoom,
  findRejoinableGames, convertPlayerToRealBot,
  getAvailableRooms, getWatchableRooms, createBotFightRoom,
  getAvailableBots, getProfileSettingsBatch,
  subscribeToRoomsList,
  kickPlayerFromRoom, sendMessage,
} from '../utils/supabaseActions';
import { mapRoomError } from '../utils/errorMapper';

const hasReclaimableSeat = (room, username) => {
  if (!room || room.status !== 'playing' || !username) return false;

  const roomPlayers = Array.isArray(room.players) ? room.players : [];
  const gamePlayers = Array.isArray(room.game_state?.players) ? room.game_state.players : [];

  const stillOwnsHumanSeat = roomPlayers.some((player) => player.username === username && !player.isAI);
  const timedOutSeatExists = gamePlayers.some((player) => player.name === username && player.isAI);

  return stillOwnsHumanSeat && timedOutSeatExists;
};

/**
 * useRoomLobby — owns all room / lobby state and handlers.
 *
 * Params:
 *   roomCode, setRoomCode, roomData, setRoomData     — from App.js state
 *   isSpectator, setIsSpectator                      — from App.js state
 *   setRejoinableGames                               — from App.js state
 *   numPlayers                                       — from App.js state
 *   screen, setScreen                                — from App.js state
 *   currentUser                                      — from App.js state
 *   setLastExecutedAITurn                            — from useAIPlayer
 *   gameOrchestratorRef                              — ref populated after useGameOrchestrator runs;
 *                                                      contains game state setters + refs needed by
 *                                                      handleWatchRoom / handleRejoinGame / handleStartOnlineGame
 *   t, addToast, clearRoomChat                       — utilities
 */
export function useRoomLobby({
  roomCode,
  setRoomCode,
  roomData,
  setRoomData,
  isSpectator: _isSpectator,
  isBotFight,
  setIsSpectator,
  setIsBotFight,
  setRejoinableGames,
  numPlayers,
  screen,
  setScreen,
  currentUser,
  setLastExecutedAITurn,
  gameOrchestratorRef,
  t,
  addToast,
  clearRoomChat,
}) {
  // ── Room-local state ──────────────────────────────────────────────────────
  const [joinCodeInput, setJoinCodeInput]           = useState('');
  const [availableRooms, setAvailableRooms]         = useState([]);
  const [watchableRooms, setWatchableRooms]         = useState([]);
  const [roomListTab, setRoomListTab]               = useState('open');
  const [mapAlgorithm, setMapAlgorithm]             = useState('corridors');
  const [isPrivateRoom, setIsPrivateRoom]           = useState(false);
  const [playerPreferredColors, setPlayerPreferredColors] = useState({});
  const [isCreatingRoom, setIsCreatingRoom]         = useState(false);
  const [isJoiningRoom, setIsJoiningRoom]           = useState(false);
  const [isStartingGame, setIsStartingGame]         = useState(false);
  const [isAddingAI, setIsAddingAI]                 = useState(false);
  const [isCreatingBotFight, setIsCreatingBotFight] = useState(false);
  const [isRematching, setIsRematching]             = useState(false);
  const [readyCooldownUntil, setReadyCooldownUntil] = useState(0);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Load available rooms when viewing the lobby (Realtime instead of polling)
  useEffect(() => {
    if (screen === 'create-or-join') {
      loadAvailableRooms();
      loadWatchableRooms();

      let debounceTimer = null;
      const unsubscribe = subscribeToRoomsList(() => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          loadAvailableRooms();
          loadWatchableRooms();
        }, 1500);
      });

      return () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        unsubscribe();
      };
    }
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load preferred colors for all players in room (single batch query)
  useEffect(() => {
    if (roomData?.players && roomData.players.length > 0) {
      const loadPlayerColors = async () => {
        const humanPlayers = roomData.players.filter(p => !p.isAI);
        const usernames = humanPlayers.map(p => p.username);

        try {
          const settingsMap = await getProfileSettingsBatch(usernames);
          const colors = {};
          for (const player of roomData.players) {
            if (!player.isAI) {
              const settings = settingsMap[player.username];
              colors[player.username] = settings ? parseInt(settings.preferredColorIndex, 10) || 0 : 0;
            } else {
              colors[player.username] = null;
            }
          }
          setPlayerPreferredColors(colors);
        } catch (err) {
          console.warn('Failed to batch-load player colors:', err);
          const colors = {};
          for (const player of roomData.players) {
            colors[player.username] = player.isAI ? null : 0;
          }
          setPlayerPreferredColors(colors);
        }
      };

      loadPlayerColors();
    }
    // Justification: intentionally fires only on player-count change, not every roomData update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomData?.players?.length]);

  // ── Room list loaders ──────────────────────────────────────────────────────

  const loadAvailableRooms = async () => {
    try {
      // Room cleanup runs server-side via pg_cron; the client only reads rooms here.
      const data = await getAvailableRooms();
      setAvailableRooms(data);
    } catch (e) {
      console.error('Failed to load available rooms:', e);
      setAvailableRooms([]);
    }
  };

  const loadWatchableRooms = async () => {
    try {
      const data = await getWatchableRooms();
      setWatchableRooms(data);
    } catch (e) {
      console.error('Failed to load watchable rooms:', e);
      setWatchableRooms([]);
    }
  };

  // ── Room handlers ──────────────────────────────────────────────────────────

  const SWAP_LIMIT_OPTIONS = [null, 5];
  const randomSwapLimit = () => SWAP_LIMIT_OPTIONS[Math.floor(Math.random() * SWAP_LIMIT_OPTIONS.length)];

  const resetFinishedGameUi = () => {
    const go = gameOrchestratorRef.current;
    go.setShowGameEndModal(false);
    go.setFinalGamePlayers([]);
    go.setGameEnded(false);
    go.setEndGamePlayers(null);
    go.gameEndingRef.current = false;
    go.gameLogRef.current = null;
    if (go.gameStartTimeRef) go.gameStartTimeRef.current = null;
  };

  const buildBotFightGameState = (bots, algorithm, settings = {}) => {
    const actualNumBots = bots.length;
    const markersPerPlayer = 20;
    const mapScale = 15;
    const board = generateMap(actualNumBots, mapScale, mapScale, markersPerPlayer, algorithm);
    const assignedColorIndices = bots.map((_, index) => index);

    const allMarkers = [];
    for (let i = 0; i < actualNumBots; i++) {
      for (let j = 0; j < markersPerPlayer; j++) {
        allMarkers.push({ colorIndex: assignedColorIndices[i] });
      }
    }

    const shuffle = (arr) => {
      const next = [...arr];
      for (let i = next.length - 1; i > 0; i--) {
        const randomIndex = Math.floor(Math.random() * (i + 1));
        [next[i], next[randomIndex]] = [next[randomIndex], next[i]];
      }
      return next;
    };

    const shuffled = shuffle(allMarkers);
    const newPlayers = bots.map((bot, index) => ({
      name: bot.username,
      colorIndex: assignedColorIndices[index],
      markers: shuffled.slice(index * 3, index * 3 + 3),
      score: 0,
      isAI: true,
      isBot: true,
      bot_persona: bot.bot_persona || 'v1',
      rating: bot.rating || 1500,
      games_played: 0,
    }));

    return {
      gameBoard: board,
      players: newPlayers,
      currentPlayer: Math.floor(Math.random() * actualNumBots),
      markersPool: shuffled.slice(actualNumBots * 3),
      settings,
      consecutiveSwaps: 0,
      moves: [],
    };
  };

  const launchBotFightRoom = async (bots, algorithm, settings = {}) => {
    if (!bots || bots.length < 2) {
      throw new Error(t('notEnoughBots'));
    }

    const gameState = buildBotFightGameState(bots, algorithm, settings);
    const room = await createBotFightRoom(gameState, algorithm, settings);
    const go = gameOrchestratorRef.current;

    resetFinishedGameUi();
    go.setGameBoard(gameState.gameBoard);
    go.setPlayers(gameState.players);
    go.setCurrentPlayer(gameState.currentPlayer);
    go.setMarkersPool(gameState.markersPool);
    go.setMoveHistory([]);
    go.gameLogRef.current = createGameLog(
      gameState.gameBoard,
      gameState.players,
      gameState.markersPool,
      gameState.currentPlayer
    );
    if (go.gameStartTimeRef) go.gameStartTimeRef.current = Date.now();

    clearRoomChat();
    setLastExecutedAITurn(null);
    setRoomCode(room.code);
    setRoomData(room);
    setIsSpectator(false);
    setIsBotFight(true);
    setScreen('game');

    return room;
  };

  const handleCreateRoom = async () => {
    setIsCreatingRoom(true);
    try {
      const settings = {
        swapLimitPerPlayer: randomSwapLimit(),
        turnTimerSeconds: DEFAULT_TURN_TIMER_SECONDS,
      };
      const room = await createRoom(currentUser, numPlayers, mapAlgorithm, isPrivateRoom, settings);
      setRoomCode(room.code);
      setRoomData(room);
      setScreen('room');
    } catch (e) {
      addToast(mapRoomError(e, t, 'errCreateRoom'), 'error');
    } finally {
      setIsCreatingRoom(false);
    }
  };

  const handleCreateBotFight = async (botFightElo, numBots = 8) => {
    setIsCreatingBotFight(true);
    try {
      // 1. Fetch bots close to the requested ELO bracket
      const bots = await getAvailableBots(botFightElo, numBots);
      if (!bots || bots.length < 2) {
        addToast(t('notEnoughBots'), 'error');
        return;
      }

      const algorithm = mapAlgorithm || 'corridors';
      const botFightSettings = { swapLimitPerPlayer: randomSwapLimit() };
      await launchBotFightRoom(bots, algorithm, botFightSettings);
    } catch (e) {
      addToast(mapRoomError(e, t, 'errCreateRoom'), 'error');
    } finally {
      setIsCreatingBotFight(false);
    }
  };

  const handleRematch = async () => {
    if (!roomData || isRematching) return;

    setIsRematching(true);
    try {
      const rematchSettings = resolveRoomSettings(roomData, {
        defaultSwapLimitPerPlayer: null,
        defaultTurnTimerSeconds: DEFAULT_TURN_TIMER_SECONDS,
      });
      const algorithm = roomData.map_algorithm || 'corridors';

      if (isBotFight || roomData.is_bot_fight) {
        const sourcePlayers = roomData.game_state?.players || roomData.players || [];
        const bots = sourcePlayers
          .filter(player => player?.isAI || player?.isBot)
          .map((player, index) => ({
            username: player.name || player.username || `Bot ${index + 1}`,
            rating: player.rating || 1500,
            bot_persona: player.bot_persona || 'v1',
          }));

        if (bots.length < 2) {
          addToast(t('notEnoughBots'), 'error');
          return;
        }

        await launchBotFightRoom(bots, algorithm, rematchSettings);
        return;
      }

      const rematchRoom = await createOrJoinRematch(roomCode);

      resetFinishedGameUi();
      clearRoomChat();
      setRoomCode(rematchRoom.code);
      setRoomData(rematchRoom);
      setIsSpectator(false);
      setIsBotFight(false);
      setScreen('room');

      const toastKey = rematchRoom.host === currentUser ? 'rematchCreated' : 'rematchJoined';
      addToast(t(toastKey).replace('{code}', rematchRoom.code), 'success', 6000);
    } catch (e) {
      addToast(mapRoomError(e, t, 'errCreateRoom'), 'error');
    } finally {
      setIsRematching(false);
    }
  };

  const handleJoinRoom = async () => {
    const code = joinCodeInput.trim().toUpperCase();
    if (!code) return;
    setIsJoiningRoom(true);
    try {
      const room = await joinRoom(code, currentUser);
      setRoomCode(code);
      setRoomData(room);
      setScreen('room');
    } catch (e) {
      addToast(mapRoomError(e, t, 'errJoinRoom'), 'error');
    } finally {
      setIsJoiningRoom(false);
    }
  };

  const handleQuickJoinRoom = async (code) => {
    setIsJoiningRoom(true);
    try {
      const room = await joinRoom(code, currentUser);
      setRoomCode(code);
      setRoomData(room);
      setScreen('room');
    } catch (e) {
      addToast(mapRoomError(e, t, 'errJoinRoom'), 'error');
    } finally {
      setIsJoiningRoom(false);
    }
  };

  const handleQuickPlay = async () => {
    setIsJoiningRoom(true);
    try {
      // Try to join an existing open public room first
      const openRooms = await getAvailableRooms();
      if (openRooms && openRooms.length > 0) {
        const target = openRooms[0];
        const room = await joinRoom(target.code, currentUser);
        setRoomCode(target.code);
        setRoomData(room);
        setScreen('room');
        return;
      }
      // No open rooms — create a new public room with default settings
      const room = await createRoom(currentUser, 5, 'corridors', false, {
        swapLimitPerPlayer: randomSwapLimit(),
        turnTimerSeconds: DEFAULT_TURN_TIMER_SECONDS,
      });
      setRoomCode(room.code);
      setRoomData(room);
      setScreen('room');
    } catch (e) {
      addToast(mapRoomError(e, t, 'errCreateRoom'), 'error');
    } finally {
      setIsJoiningRoom(false);
    }
  };

  const handleWatchRoom = async (code) => {
    try {
      const { data: room, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('code', code)
        .single();
      if (error) throw error;

      if (hasReclaimableSeat(room, currentUser)) {
        await handleRejoinGame(code);
        return;
      }

      // Reset stale game state via ref (avoids circular hook dependency)
      const go = gameOrchestratorRef.current;
      go.setShowGameEndModal(false);
      go.setFinalGamePlayers([]);
      go.setGameEnded(false);
      go.setEndGamePlayers(null);
      go.gameEndingRef.current = false;
      setLastExecutedAITurn(null);

      setRoomCode(code);
      setRoomData(room);
      setIsSpectator(true);

      if (room.status === 'playing' && room.game_state) {
        const gs = room.game_state;
        go.setGameBoard(gs.gameBoard);
        go.setPlayers(gs.players || []);
        go.setCurrentPlayer(gs.currentPlayer || 0);
        go.setMarkersPool(gs.markersPool || []);
        go.setMoveHistory(gs.moves || []);
        setScreen('game');
      }
    } catch (e) {
      addToast(mapRoomError(e, t, 'errWatchRoom'), 'error');
    }
  };

  const handleStopWatching = () => {
    setRoomCode('');
    setRoomData(null);
    setIsSpectator(false);
    clearRoomChat();
    setScreen('create-or-join');
  };

  const refreshRejoinablesAndRoute = async () => {
    const activeGames = await findRejoinableGames(currentUser);
    const nextScreen = activeGames.length > 0 ? 'rejoin-or-new' : 'create-or-join';
    setRejoinableGames(activeGames);
    setScreen(nextScreen);
    return activeGames;
  };

  const handleRejoinGame = async (gameRoomCode) => {
    try {
      const room = await rejoinGame(gameRoomCode, currentUser);
      setRoomCode(gameRoomCode);
      setRoomData(room);
      setIsSpectator(false);
      setIsBotFight(false);

      if (room.status === 'waiting') {
        setScreen('room');
        return;
      }

      if (room.status === 'playing' && room.game_state) {
        const gs = room.game_state;
        const go = gameOrchestratorRef.current;
        go.setGameBoard(gs.gameBoard);
        go.setPlayers(gs.players);
        go.setCurrentPlayer(gs.currentPlayer);
        go.setMarkersPool(gs.markersPool);
        go.setMoveHistory(gs.moves || []);
        go.setGameEnded(false);
        setLastExecutedAITurn(null);

        // Reconstruct game log from server initial state + replay server moves
        if (gs.game_log_initial) {
          const expanded = expandGameLog(gs.game_log_initial);
          if (gs.moves && gs.gameBoard) {
            for (const sm of gs.moves) {
              if (sm.type === 'place') {
                const ci = typeof gs.gameBoard[sm.row]?.[sm.col] === 'number'
                  ? gs.gameBoard[sm.row][sm.col] : null;
                if (ci !== null) recordPlaceMove(expanded, sm.player, ci, sm.row, sm.col);
              } else if (sm.type === 'swap') {
                recordSwapMove(expanded, sm.player, sm.target);
              }
            }
          }
          go.gameLogRef.current = expanded;
          try { localStorage.setItem(`gamelog_${gameRoomCode}`, JSON.stringify(expanded)); } catch { /* ignore */ }
        }

        setScreen('game');
      }
    } catch (e) {
      if (e?.message === 'Room not found') {
        try {
          await refreshRejoinablesAndRoute();
          setRoomCode('');
          setRoomData(null);
          setIsSpectator(false);
          clearRoomChat();
        } catch {
          setRejoinableGames([]);
          setScreen('create-or-join');
        }
      }
      addToast(mapRoomError(e, t, 'errRejoinGame'), 'error');
    }
  };

  const handleLeaveRoom = async () => {
    if (!roomCode) return;
    try {
      if (screen === 'game') {
        const gsPlayers = roomData?.game_state?.players || [];
        const meInGame = gsPlayers.find(p => p.name === currentUser);
        const myRating = meInGame?.rating || 1500;
        await convertPlayerToRealBot(roomCode, currentUser, myRating);
        addToast(t('errReplacedByAI'), 'info', 6000);
        setRoomCode('');
        setRoomData(null);
        setIsSpectator(false);
        clearRoomChat();
        setScreen('create-or-join');
      } else {
        await leaveRoom(roomCode, currentUser);
        clearRoomChat();

        await refreshRejoinablesAndRoute();
        setRoomCode('');
        setRoomData(null);
        setIsSpectator(false);
      }
    } catch (e) {
      console.error('Leave room error:', e);
      addToast(mapRoomError(e, t, 'errLeaveRoom'), 'error');
    }
  };

  const handleToggleReady = async () => {
    if (Date.now() < readyCooldownUntil) return;
    const until = Date.now() + 3000;
    setReadyCooldownUntil(until);
    setTimeout(() => setReadyCooldownUntil(0), 3000);
    try {
      await togglePlayerReady(roomCode, currentUser);
    } catch (e) {
      console.error('Toggle ready error:', e);
    }
  };

  const handleKickPlayer = async (usernameToKick) => {
    try {
      await kickPlayerFromRoom(roomCode, usernameToKick);
      sendMessage(roomCode, currentUser, t('kickedNotifMsg').replace('{user}', usernameToKick)).catch(() => {});
    } catch (e) {
      console.error('Kick player error:', e);
      addToast(mapRoomError(e, t, 'errKickPlayer'), 'error');
    }
  };

  const handleAddAI = async () => {
    if (isAddingAI) return;
    setIsAddingAI(true);
    try {
      if (!roomData) return;
      const slotsToFill = roomData.num_players - (roomData.players ? roomData.players.length : 0);
      if (slotsToFill <= 0) return;

      const humanPlayers = (roomData.players || []).filter(p => !p.isAI);
      let avgElo = 1500;
      if (humanPlayers.length > 0) {
        const humanUsernames = humanPlayers.map(p => p.username);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('username, rating')
          .in('username', humanUsernames);
        const ratingMap = {};
        (profiles || []).forEach(p => { ratingMap[p.username] = p.rating || 1500; });
        const totalElo = humanUsernames.reduce((sum, u) => sum + (ratingMap[u] || 1500), 0);
        avgElo = Math.round(totalElo / humanUsernames.length);
      }

      const bots = await getAvailableBots(avgElo, slotsToFill);
      if (bots.length === 0) {
        addToast(t('errNoBots'), 'warning');
        return;
      }

      for (const bot of bots) {
        await addBotToRoom(roomCode, bot);
      }

      // Optimistic refresh: don't wait for the Realtime subscription — it may
      // arrive 200-1000ms late, leaving the player list visually stale while
      // isAddingAI is already false.  A direct DB fetch guarantees the updated
      // players array is shown immediately after the last bot is added.
      const { data: freshRoom } = await supabase
        .from('rooms')
        .select('*')
        .eq('code', roomCode)
        .single();
      if (freshRoom) setRoomData(freshRoom);
    } catch (e) {
      addToast(mapRoomError(e, t, 'errAddBot'), 'error');
    } finally {
      setIsAddingAI(false);
    }
  };

  // ── Game start ─────────────────────────────────────────────────────────────

  // Assign colors to players respecting their preferences (FIFO)
  const assignColorsWithPreferences = (roomPlayers, userSettings) => {
    const assignedColors = new Array(roomPlayers.length);
    const usedColorIndices = new Set();

    for (let i = 0; i < roomPlayers.length; i++) {
      const settings = userSettings[i] || {};
      let preferredColorIndex = settings.preferredColorIndex;

      if (preferredColorIndex !== undefined) {
        preferredColorIndex = parseInt(preferredColorIndex, 10);
      }

      if (
        preferredColorIndex !== undefined &&
        !isNaN(preferredColorIndex) &&
        !usedColorIndices.has(preferredColorIndex) &&
        preferredColorIndex >= 0 &&
        preferredColorIndex < PLAYER_COLORS.length
      ) {
        assignedColors[i] = preferredColorIndex;
        usedColorIndices.add(preferredColorIndex);
        console.log(`Player ${i} (${roomPlayers[i].username}): assigned preferred color ${preferredColorIndex}`);
      } else {
        assignedColors[i] = null;
      }
    }

    for (let i = 0; i < roomPlayers.length; i++) {
      if (assignedColors[i] === null) {
        for (let colorIdx = 0; colorIdx < PLAYER_COLORS.length; colorIdx++) {
          if (!usedColorIndices.has(colorIdx)) {
            assignedColors[i] = colorIdx;
            usedColorIndices.add(colorIdx);
            console.log(`Player ${i} (${roomPlayers[i].username}): assigned fallback color ${colorIdx}`);
            break;
          }
        }
      }
    }

    return assignedColors;
  };

  const handleStartOnlineGame = async () => {
    if (!roomData || isStartingGame) return;

    const allReady = roomData.players.every(p => p.ready);
    if (!allReady) {
      addToast(t('errNotReady'), 'warning');
      return;
    }

    setIsStartingGame(true);

    try {
      const humanUsernames = roomData.players.filter(p => !p.isAI).map(p => p.username);
      const settingsMap = await getProfileSettingsBatch(humanUsernames);
      const userSettingsArray = roomData.players.map(p =>
        p.isAI ? {} : (settingsMap[p.username] || { preferredColorIndex: 0 })
      );

      const assignedColorIndices = assignColorsWithPreferences(roomData.players, userSettingsArray);

      const humanOnlineNames = roomData.players.filter(p => !p.isAI).map(p => p.username);
      let onlineRatingMap = {};
      if (humanOnlineNames.length > 0) {
        try {
          const { data: ratingProfiles } = await supabase
            .from('profiles')
            .select('username, rating, games_played')
            .in('username', humanOnlineNames);
          (ratingProfiles || []).forEach(p => {
            onlineRatingMap[p.username] = {
              rating: p.rating || 1500,
              games_played: p.games_played || 0,
            };
          });
        } catch (e) {
          console.warn('Could not fetch player ratings:', e);
        }
      }

      const mapScale = 15;
      const markersPerPlayer = 20;
      const algorithm = roomData.map_algorithm || 'corridors';
      const board = generateMap(roomData.num_players, mapScale, mapScale, markersPerPlayer, algorithm);

      const allMarkers = [];
      for (let i = 0; i < roomData.players.length; i++) {
        const assignedColorIndex = assignedColorIndices[i];
        for (let j = 0; j < markersPerPlayer; j++) {
          allMarkers.push({ colorIndex: assignedColorIndex });
        }
      }

      const shuffle = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
      };
      const shuffled = shuffle(allMarkers);

      const newPlayers = [];
      let markerIndex = 0;

      for (let i = 0; i < roomData.players.length; i++) {
        const roomPlayer = roomData.players[i];
        const assignedColorIndex = assignedColorIndices[i];
        const playerMarkers = shuffled.slice(markerIndex, markerIndex + 3);

        console.log(`Player ${i} (${roomPlayer.username}): assigned player color ${assignedColorIndex}, random initial markers:`, playerMarkers);

        if (roomPlayer.isAI) {
          newPlayers.push({
            name: roomPlayer.username,
            colorIndex: assignedColorIndex,
            markers: playerMarkers,
            score: 0,
            isAI: true,
            isBot: true,
            bot_persona: roomPlayer.bot_persona || 'v1',
            rating: roomPlayer.rating || 1500,
            games_played: 0,
          });
        } else {
          const onlinePlayerRating = onlineRatingMap[roomPlayer.username];
          newPlayers.push({
            name: roomPlayer.username,
            colorIndex: assignedColorIndex,
            markers: playerMarkers,
            score: 0,
            isAI: false,
            rating: onlinePlayerRating?.rating || 1500,
            games_played: onlinePlayerRating?.games_played || 0,
          });
        }
        markerIndex += 3;
      }

      const gameState = {
        gameBoard: board,
        players: newPlayers,
        currentPlayer: Math.floor(Math.random() * roomData.players.length),
        markersPool: shuffled.slice(markerIndex),
        settings: roomData.settings || { swapLimitPerPlayer: null },
        consecutiveSwaps: 0,
        moves: [],
      };

      const go = gameOrchestratorRef.current;
      go.gameLogRef.current = createGameLog(
        gameState.gameBoard, gameState.players, gameState.markersPool, gameState.currentPlayer
      );
      try {
        localStorage.setItem(`gamelog_${roomCode}`, JSON.stringify(go.gameLogRef.current));
      } catch { /* ignore */ }

      // Include game_log_initial so non-hosts and rejoiners can reconstruct the game log
      const gameStateForServer = {
        ...gameState,
        game_log_initial: finalizeGameLog(go.gameLogRef.current),
      };
      await startGame(roomCode, gameStateForServer);

      setRoomData(prev => prev ? {
        ...prev,
        status: 'playing',
        game_state: gameStateForServer,
      } : prev);

      // Directly transition the host to the game screen (avoids reliance on Realtime)
      go.setGameBoard(gameState.gameBoard);
      go.setPlayers(gameState.players);
      go.setCurrentPlayer(gameState.currentPlayer);
      go.setMarkersPool(gameState.markersPool);
      go.setMoveHistory([]);
      go.setGameEnded(false);
      setLastExecutedAITurn(null);
      setScreen('game');
    } catch (e) {
      addToast(mapRoomError(e, t, 'errStartGame'), 'error');
    } finally {
      setIsStartingGame(false);
    }
  };

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State
    joinCodeInput, setJoinCodeInput,
    availableRooms,
    watchableRooms,
    roomListTab, setRoomListTab,
    mapAlgorithm, setMapAlgorithm,
    isPrivateRoom, setIsPrivateRoom,
    playerPreferredColors,
    isCreatingRoom,
    isJoiningRoom,
    isStartingGame,
    isAddingAI,
    isCreatingBotFight,
    readyCooldownUntil,
    // Handlers
    loadAvailableRooms,
    loadWatchableRooms,
    handleCreateRoom,
    handleCreateBotFight,
    handleRematch,
    handleJoinRoom,
    handleQuickJoinRoom,
    handleQuickPlay,
    handleWatchRoom,
    handleStopWatching,
    handleRejoinGame,
    handleLeaveRoom,
    handleToggleReady,
    handleKickPlayer,
    handleAddAI,
    handleStartOnlineGame,
  };
}
